import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  Spinner,
} from "@chakra-ui/react";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import useCreateOrEditLesson from "../../hooks/api/lesson/useCreateOrEditLesson";
import useGetModules from "../../hooks/api/lesson/useGetModules";
import { Lesson } from "../../models/Lesson";
import { useTranslation } from "react-i18next";

type FormValues = {
  name: string;
  description: string;
  position: number;
  moduleId: number;
};

type Props = {
  lesson?: Lesson;
  learningPathId?: string;
  onSuccess?: (lessonId: number) => void;
  onError?: () => void;
};

export default function LessonForm({
  learningPathId,
  lesson,
  onSuccess,
  onError,
}: Props) {
  const { t } = useTranslation();
  const {
    handleSubmit,
    register,
    formState: { errors, isValid, isSubmitted },
  } = useForm<FormValues>({
    defaultValues: {
      name: lesson?.name || "",
      description: lesson?.description || "",
      position: lesson?.position,
      moduleId: lesson?.module.id,
    },
  });
  const { data: modules, isLoading: areModulesLoading } = useGetModules();

  const { mutateAsync: createOrEditLesson, isPending } = useCreateOrEditLesson(
    learningPathId ?? "",
  );

  const onSubmit = useCallback(
    (values: FormValues) => {
      createOrEditLesson({
        lesson: { id: lesson?.id, ...values },
        operation: lesson ? "edit" : "create",
      })
        .then((response) => onSuccess?.(response.id))
        .catch(onError);
    },
    [createOrEditLesson, lesson, onError, onSuccess],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormControl
        isInvalid={!isValid && isSubmitted}
        display="flex"
        flexDirection="column"
        gap={4}
      >
        <div>
          <FormLabel htmlFor="name">{t("forms.name")}</FormLabel>
          <Input
            id="name"
            placeholder={t("forms.name")}
            {...register("name", { required: t("forms.validation.required") })}
          />
          <FormErrorMessage>
            {errors.name && errors.name.message}
          </FormErrorMessage>
        </div>
        <div>
          <FormLabel htmlFor="description">{t("forms.description")}</FormLabel>
          <Input
            id="description"
            placeholder={t("forms.description")}
            {...register("description", {
              required: t("forms.validation.required"),
            })}
          />
          <FormErrorMessage>
            {errors.description && errors.description.message}
          </FormErrorMessage>
        </div>
        <div>
          <FormLabel htmlFor="position">{t("forms.position")}</FormLabel>
          <Input
            type="number"
            id="position"
            placeholder={t("forms.position")}
            {...register("position", {
              required: t("forms.validation.required"),
            })}
          />
          <FormErrorMessage>
            {errors.position && errors.position.message}
          </FormErrorMessage>
        </div>
        <div>
          <FormLabel htmlFor="moduleId">{t("forms.category")}</FormLabel>
          {modules?.content.length ? (
            <Select
              id="category"
              {...register("moduleId", {
                required: t("forms.validation.required"),
              })}
            >
              <option value="">-</option>
              {modules.content.map((module) => (
                <option key={module.id} value={module.id}>
                  {module.name}
                </option>
              ))}
            </Select>
          ) : (
            <Spinner color="primary.DEFAULT" />
          )}
          <FormErrorMessage>
            {errors.moduleId && errors.moduleId.message}
          </FormErrorMessage>
        </div>
      </FormControl>
      <Button
        mt={4}
        bg="primary.DEFAULT"
        color="white"
        disabled={areModulesLoading}
        isLoading={isPending}
        type="submit"
      >
        {t("common.buttons.save")}
      </Button>
    </form>
  );
}
